import { Alert, Button, Col, Form, Row, Spin, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type { SettlementAccountCreate } from '@repo/sdk'

import { useGetSettlementAccount } from './useGetSettlementAccount'
import { useUpsertSettlementAccount } from './useUpsertSettlementAccount'
import { SettlementAccountInfoCard } from './SettlementAccountInfoCard'
import { SettlementAccountForm } from './SettlementAccountForm'

export function SettlementAccount() {
  const { t } = useTranslation()
  const [form] = Form.useForm<SettlementAccountCreate>()
  const { account, isLoading, isNotFound, refetch } = useGetSettlementAccount()

  const { upsertAccount, isPending } = useUpsertSettlementAccount(refetch)

  const handleSubmit = () => {
    void form.validateFields().then((values) => {
      upsertAccount(values)
    })
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  const hasAccount = !!account && !isNotFound

  return (
    <div>
      <Typography.Title level={4}>{t('pages.settlements.accountTitle')}</Typography.Title>

      {!hasAccount && (
        <Typography.Paragraph type="secondary">
          {t('pages.settlements.noAccountDescription')}
        </Typography.Paragraph>
      )}

      {/* A registered account is changed only by administrators (#344): payouts run on the account
          registered with Toss, which a change here never reached. The dealer registers the first
          one. */}
      <Row gutter={[16, 16]} style={{ maxWidth: 900, margin: '0 auto' }}>
        {hasAccount ? (
          <Col xs={24} lg={12}>
            <SettlementAccountInfoCard account={account} />
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message={t('pages.settlements.changeByAdminNotice')}
            />
          </Col>
        ) : (
          <Col xs={24}>
            <SettlementAccountForm form={form} />
            <Button
              data-testid="settlement-account-save-btn"
              type="primary"
              onClick={handleSubmit}
              loading={isPending}
              style={{ marginTop: 16 }}
            >
              {t('common.buttons.save')}
            </Button>
          </Col>
        )}
      </Row>
    </div>
  )
}
